import React, { FC } from 'react'
import { useWatch, useFormContext } from 'react-hook-form'
import moment from 'moment'
import ControlledHiddenInput from 'transfix-form/components/ControlledHiddenInput'
import { DeprecatedControlledSelect } from 'transfix-form/components/ControlledSelect'
import { Box } from 'transfix-ui/core'
import { APPOINTMENT_TYPES } from 'types'
import { ShipmentStop } from 'apps/carrier/apiTypes/activeShipments'
import { getIsLate } from 'utils/trackingHelpers'
import FORMATS from 'constants/datetime'
import { LATE_REASONS } from 'constants/lateReasons'

interface ILateReasonDropdownRenderProps {
  stop: ShipmentStop
  stopTypeField: string
}

/**
 * Watches for changes to `manualEta` entry. Displays dropdown for 'late reasons' when
 * user entry is later than scheduled appointment time
 */
const LateReasonDropdownRender: FC<ILateReasonDropdownRenderProps> = ({
  stop: { appointment, stopSequence = 0, ...stop },
  stopTypeField,
}) => {
  const {
    errors,
    formState: { dirtyFields },
  } = useFormContext()
  const stopKey = stop.stopType === 'pickup' ? 'pickupsAttributes' : 'deliveriesAttributes'
  const dirtyStopFields = dirtyFields[stopKey] ? dirtyFields[stopKey][stopSequence - 1] : {}

  const manualEta: string | undefined = useWatch({
    name: `${stopTypeField}.manualEta`,
  })

  const timezone = appointment?.timezone || FORMATS.defaultTimeZone

  const startAtEtaDateTime = moment(appointment?.startAt).tz(timezone).format()
  const endAtEtaDateTime = moment(appointment?.endAt).tz(timezone).subtract(2, 'hours').format()
  const scheduledEtaDateTime =
    appointment?.appointmentType === APPOINTMENT_TYPES.FCFS ? endAtEtaDateTime : startAtEtaDateTime
  const isLateEta = manualEta
    ? Boolean(getIsLate(moment(manualEta), moment(scheduledEtaDateTime)))
    : false
  const errorMessage =
    errors[stopKey] && errors[stopKey][stopSequence - 1]
      ? errors[stopKey][stopSequence - 1].lateReason?.message
      : ''

  return (
    <>
      {isLateEta && dirtyStopFields?.manualEta && (
        <Box mt={1} mr={2}>
          <DeprecatedControlledSelect
            name={`${stopTypeField}.lateReason`}
            SelectProps={{
              dataTest: 'select-late-reason',
              options: LATE_REASONS,
              placeholder: 'Select late reason',
              id: `select-late-reasons-${stopTypeField}`,
              invalid: Boolean(errorMessage),
              validationMessage: errorMessage,
            }}
          />
          <ControlledHiddenInput name={`${stopTypeField}.hasLateReason`} defaultValue />
        </Box>
      )}
    </>
  )
}

export default LateReasonDropdownRender
